import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { Button } from "@/components/ui/button";
import ModelCardDialog from "@/components/ModelCardDialog";
import ColorVariantDialog from "@/components/ColorVariantDialog";
import ProductThumb from "@/components/ProductThumb";
import { Tag, Plus, Trash2, Pencil, Palette } from "lucide-react";
import { toast } from "sonner";

export default function Products() {
  const { products, refreshProducts } = useData();
  const [models, setModels] = useState([]);
  const [modelDialog, setModelDialog] = useState({ open: false, editing: null });
  const [colorDialog, setColorDialog] = useState({ open: false, model: null, editing: null });

  const loadModels = useCallback(async () => {
    const r = await api.get("/models");
    setModels(r.data);
  }, []);
  useEffect(() => { loadModels(); }, [loadModels, products]);

  const refreshAll = () => { refreshProducts(); loadModels(); };

  const removeColor = async (id) => {
    if (!window.confirm("Bu rengi (stok kartını) silmek istiyor musunuz?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Silindi"); refreshAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const removeModel = async (id) => {
    if (!window.confirm("Model kodunu silmek istiyor musunuz?")) return;
    try { await api.delete(`/models/${id}`); toast.success("Model silindi"); loadModels(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Ürün Tanımları</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
            <Tag className="w-7 h-7 text-[#D97706]" /> Stok Kartları
          </h1>
          <p className="text-[#6B5B4A] mt-2 text-sm">Önce model kodunu oluşturun, sonra o modelin her rengini ayrı ayrı ekleyin. Kesim ve sipariş renk bazında seçilir.</p>
        </div>
        <Button data-testid="model-new-btn" onClick={() => setModelDialog({ open: true, editing: null })}
          className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-5">
          <Plus className="w-4 h-4 mr-2" /> Yeni Model Kodu
        </Button>
      </div>

      {models.length === 0 && (
        <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-10 text-center text-[#8C7B68]">
          Henüz model yok. &ldquo;Yeni Model Kodu&rdquo; ile başlayın.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="models-list">
        {models.map((m) => {
          const colors = products.filter((p) => p.model_code === m.model_code);
          return (
            <div key={m.id} className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5" data-testid={`model-card-${m.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-[#B45309]">{m.model_code}</h3>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-[#8C7B68]">
                    {m.season || "Sezon yok"} · {colors.length} renk
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => setModelDialog({ open: true, editing: m })} data-testid={`model-edit-${m.id}`}
                    className="text-[#8C7B68] hover:text-[#B45309]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => removeModel(m.id)} data-testid={`model-delete-${m.id}`}
                    className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="rounded-md border border-[#E8DDCA] divide-y divide-[#E8DDCA] mb-3">
                {colors.length === 0 && <p className="px-3 py-3 text-sm text-[#8C7B68]">Bu modele henüz renk eklenmedi.</p>}
                {colors.map((p) => (
                  <div key={p.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-[#EFE6D6]/60" data-testid={`product-row-${p.id}`}>
                    <div className="min-w-0 flex items-center gap-3">
                      <ProductThumb product={p} size="w-12 h-12" testId={`product-thumb-${p.id}`} />
                      <div className="min-w-0">
                      <p className="font-medium flex items-center gap-2"><Palette className="w-3.5 h-3.5 text-[#D97706]" />{p.model_color}</p>
                      <p className="text-[11px] font-mono text-[#8C7B68]">
                        Taban: {p.sole_code ? `${p.sole_code}${p.sole_color ? " · " + p.sole_color : ""}` : "—"}
                      </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setColorDialog({ open: true, model: m, editing: p })} data-testid={`product-edit-${p.id}`}
                        className="text-[#8C7B68] hover:text-[#B45309]"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => removeColor(p.id)} data-testid={`product-delete-${p.id}`}
                        className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={() => setColorDialog({ open: true, model: m, editing: null })}
                data-testid={`model-add-color-${m.id}`}
                className="w-full border-dashed border-[#D97706]/60 bg-transparent text-[#B45309] hover:bg-[#EFE6D6] hover:text-[#B45309]">
                <Plus className="w-4 h-4 mr-2" /> Renk Ekle
              </Button>
            </div>
          );
        })}
      </div>

      <ModelCardDialog open={modelDialog.open} editing={modelDialog.editing}
        onOpenChange={(v) => setModelDialog((d) => ({ ...d, open: v }))}
        onSaved={(m, isNew) => { refreshAll(); if (isNew) setColorDialog({ open: true, model: m, editing: null }); }} />
      <ColorVariantDialog open={colorDialog.open} model={colorDialog.model} editing={colorDialog.editing}
        onOpenChange={(v) => setColorDialog((d) => ({ ...d, open: v }))} onSaved={refreshAll} />
    </div>
  );
}
